import { useMemo } from 'react'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { toast } from 'components/global/CUK/Toast/Toast'
import { 
  OPERATION_ACTIVATE_DEACTIVATE_PER_BATCH,
  OPERATION_RESEND_CONFIRMATION_PER_BATCH,
  OPERATION_ADD_REMOVE_ROLES_PER_BATCH
} from './operations-gql/mutations'
import { useContactsSelected, useRolesModifications } from './store'
import { trackEvent } from 'analytics'

interface BatchOperationsResult {
  activateUsers: () => Promise<void>
  deactivateUsers: () => Promise<void>
  resendConfirmations: () => Promise<void>
  updateRoles: () => Promise<void>
  isLoading: boolean
  error: string | null
}

interface UseBatchOperationsProps {
  onRefetch?: () => void
}

export const useBatchOperations = (props?: UseBatchOperationsProps): BatchOperationsResult => {
  const { t } = useTranslation()
  const [contactsSelected, setContactsSelected] = useContactsSelected()
  const [rolesModifications, setRolesModifications] = useRolesModifications()

  const validUserIds = useMemo(() => {
    return (contactsSelected)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((contact: any) => contact.sync && contact.center_user_id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((contact: any) => contact.center_user_id)
  }, [contactsSelected])

  const handleSuccess = (count: number, messageKey: string) => {
    toast.success(t(messageKey, { count }))
    setContactsSelected([])

    if (props?.onRefetch) {
      props.onRefetch()
    }
  }

  const [activateDeactivate, { loading: statusLoading }] = useMutation(
    OPERATION_ACTIVATE_DEACTIVATE_PER_BATCH,
    {
      onCompleted: () => {
        const count = validUserIds.length
        handleSuccess(count, 'auth.card_in_mass.status.success')
      },
      onError: (error) => {
        toast.error(error.message)
      }
    }
  )

  const [resendConfirmation, { loading: emailLoading }] = useMutation(
    OPERATION_RESEND_CONFIRMATION_PER_BATCH,
    {
      onCompleted: () => {
        const count = validUserIds.length
        handleSuccess(count, 'auth.card_in_mass.email.success')
      },
      onError: (error) => {
        toast.error(error.message)
      }
    }
  )

  const [addRemoveRoles, { loading: rolesLoading }] = useMutation(
    OPERATION_ADD_REMOVE_ROLES_PER_BATCH,
    {
      onCompleted: () => {
        const count = validUserIds.length
        toast.success(t('auth.card_in_mass.roles.success', { count }))
        setContactsSelected([])
        setRolesModifications({})

        if (props?.onRefetch) {
          props.onRefetch()
        }
      },
      onError: (error) => {
        toast.error(error.message)
      }
    }
  )

  const activateUsers = async () => {
    if (validUserIds.length === 0) {
      toast.error(t('auth.card_in_mass.no_synced_users'))
      return
    }

    const result = await activateDeactivate({
      variables: {
        user_ids: validUserIds,
        action_type: 'activate'
      }
    })
    if (!result.errors?.length) {
      trackEvent('users_activated', {
        action_scope: 'bulk',
        item_count: validUserIds.length
      })
    }
  }

  const deactivateUsers = async () => {
    if (validUserIds.length === 0) {
      toast.error(t('auth.card_in_mass.no_synced_users'))
      return
    }

    const result = await activateDeactivate({
      variables: {
        user_ids: validUserIds,
        action_type: 'deactivate'
      }
    })
    if (!result.errors?.length) {
      trackEvent('users_deactivated', {
        action_scope: 'bulk',
        item_count: validUserIds.length
      })
    }
  }

  const resendConfirmations = async () => {
    if (validUserIds.length === 0) {
      toast.error(t('auth.card_in_mass.no_synced_users'))
      return
    }

    const result = await resendConfirmation({
      variables: {
        user_ids: validUserIds
      }
    })
    if (!result.errors?.length) {
      trackEvent('user_confirmations_resent', {
        action_scope: 'bulk',
        item_count: validUserIds.length
      })
    }
  }

  const updateRoles = async () => {
    if (validUserIds.length === 0) {
      toast.error(t('auth.card_in_mass.no_changes'))
      return
    }

    const rolesToAdd = Object.entries(rolesModifications)
      .filter(([, state]) => state === 'add')
      .map(([role]) => role)

    const rolesToRemove = Object.entries(rolesModifications)
      .filter(([, state]) => state === 'remove')
      .map(([role]) => role)

    if (rolesToAdd.length === 0 && rolesToRemove.length === 0) {
      toast.error(t('auth.card_in_mass.no_changes'))
      return
    }

    const result = await addRemoveRoles({
      variables: {
        user_ids: validUserIds,
        roles_to_add: rolesToAdd.length > 0 ? rolesToAdd : undefined,
        roles_to_remove: rolesToRemove.length > 0 ? rolesToRemove : undefined
      }
    })
    if (!result.errors?.length) {
      trackEvent('user_roles_updated', {
        action_scope: 'bulk',
        item_count: validUserIds.length,
        roles_added_count: rolesToAdd.length,
        roles_removed_count: rolesToRemove.length
      })
    }
  }

  return {
    activateUsers,
    deactivateUsers,
    resendConfirmations,
    updateRoles,
    isLoading: statusLoading || emailLoading || rolesLoading,
    error: null
  }
}
